import ReactGA from 'react-ga4';
import { hasAuthParams } from 'react-oidc-context';
import { reportablePageAddress } from 'Utils/AnalyticsPageAddress';

// Google Analytics and the Google Ads tag, and what they are told about the
// page. Left to themselves both tags read the page address from the browser,
// query string and all, which on the email verification link is the person's
// email address and confirmation code. Every call here hands them the address
// with personal values removed instead (see AnalyticsPageAddress).

const GOOGLE_ADS_TAG_ID = 'AW-11476609062';

const currentPageAddress = () => reportablePageAddress(window.location.href, document.referrer);

// The tag library reads only entries that are `arguments` objects, not arrays.
const pushTagCommand: (...command: unknown[]) => void = function () {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
};

let analyticsMeasurementId: string | undefined;
let analyticsStarted = false;

// The page a Google or Apple sign-in returns to carries a one-time sign-in
// code in its address until the sign-in library has used it.
const addressHoldsSignInCode = (): boolean => hasAuthParams();

/**
 * Load Google Analytics, telling it the cleaned address of the page it loads on.
 *
 * The address is set as the default before anything else is queued, and given
 * again on the configuration itself. The default has to come first: the Google
 * tag can carry further destinations set up on Google's side (a second
 * Analytics property is attached today), and each sends its own first page
 * view using whatever default is in place when the tag loads.
 */
const startGoogleAnalytics = (measurementId: string): void => {
  const { page_location, page_referrer } = currentPageAddress();
  window.dataLayer = window.dataLayer || [];
  pushTagCommand('set', { page_location, page_referrer });
  ReactGA.initialize(measurementId, { gtagOptions: { page_location, page_referrer } });
  analyticsStarted = true;
};

/**
 * Start Google Analytics, now or as soon as the address is fit to be seen.
 *
 * Once loaded, the tag watches the address by itself and reports every change
 * together with the address it changed from, whatever defaults are set. So it
 * is not loaded on the page a Google or Apple sign-in returns to; it starts on
 * the first page reported after the sign-in code has left the address.
 */
export const initializeGoogleAnalytics = (measurementId: string): void => {
  analyticsMeasurementId = measurementId;
  if (!addressHoldsSignInCode()) startGoogleAnalytics(measurementId);
};

/**
 * Report the current page to the Google Ads tag and to Google Analytics. Call
 * once per navigation, with the page's path.
 */
export const reportPageToGoogleTags = (pathname: string): void => {
  if (addressHoldsSignInCode()) return;
  if (analyticsMeasurementId && !analyticsStarted) startGoogleAnalytics(analyticsMeasurementId);
  // Initialize Google Tracking Tag / Analytics (only once)
  if (!window.dataLayer) {
    window.dataLayer = [];
    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(args);
    };
    window.gtag('js', new Date());
  }
  const { page_location, page_path, page_referrer } = currentPageAddress();
  // The default for every later hit from this page (conversions, events), so
  // none of them falls back to the address in the browser.
  window.gtag('set', { page_location, page_referrer });
  // Track page views:
  window.gtag('config', GOOGLE_ADS_TAG_ID, { page_path, page_location, page_referrer });
  ReactGA.send({ hitType: 'pageview', page: pathname });
};
